import React, { useEffect, useRef, useState } from 'react';
import './styles.scss';
import useWindowSize from './hooks/useWindowSize';
import PipPlayer from 'containers/Player/components/PictureInPicture/components/PipPlayer';
import { useDispatch, useSelector } from 'react-redux';
import getEncode from 'containers/Player/utils/getEncode';
import fetchCertificate from 'containers/Player/utils/fetchCertificate';
import DefaultPlaybackConfig from 'containers/Player/utils/DefaultPlaybackConfig';
import getRequestFilters from 'containers/Player/utils/getRequestFilters';
import getResponseFilters from 'containers/Player/utils/getResponseFilters';
import { selectMediaCertificate } from 'containers/Player/selectors/media.selector';
import shaka from 'shaka-player';
import { GetRegion } from 'constants.js';
import useTracking from 'containers/Player/hooks/useTracking';
import { setBookmarkDiscard } from 'containers/Player/actions/bookmark/bookmark.actions';
import { usePIPSubtitles } from './hooks/usePIPSubtitles';
import { setAvailableCaptions } from 'containers/Player/actions/optionButtons/languageOptions.actions';
import { useNavigate } from 'react-router-dom';
import { selectMetadataKeyByRegion } from 'reducers/selectors/metadata';
import { z_index } from 'const/zIndex';

const pauseOnInitialPlay = cb => {
  let counter = 0;
  return e => {
    if (counter < 2) {
      e.target.pause();
      setTimeout(() => {
        e.target.play();
      }, 2500);
    } else {
      e.target.removeEventListener('play', pauseOnInitialPlay);
      cb();
    }
    counter += 1;
  };
};
const recalculateSubtitles = (videoTagRef, targetLanguage) => {
  setTimeout(() => {
    [...videoTagRef.textTracks].forEach(item => {
      item.mode = 'hidden';
    });
    const correct = [...videoTagRef.textTracks].filter(
      item =>
        item.language === targetLanguage.subtitle?.toLowerCase() &&
        item.cues !== null,
    );
    if (correct.length) {
      correct.forEach(showable => {
        showable.mode = 'showing';
      });
    }
  }, 2500);
};

const PictureInPicture = () => {
  const pipState = useSelector(state => state.Player.PictureInPicture);
  const apaMediaCertificate = useSelector(selectMediaCertificate);
  const videoTagRef = useRef();
  const shakaPlayerRef = useRef();
  const pictureInPictueRef = useRef();
  const pipControlsRef = useRef(null);
  const assets = useSelector(state => state.asset);
  const dispatch = useDispatch();
  const timeRef = useRef(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const media = useSelector(store => store.Player.PictureInPicture.media);
  const currentAudioLanguage = useSelector(
    state => state.Player.OptionButtons.languageOptions.currentAudioLanguage,
  );
  usePIPSubtitles({ currentAudioLanguage, pipControlsRef, videoTagRef });
  const navigate = useNavigate();

  const backOrForwardKey = useSelector(
    selectMetadataKeyByRegion('playing_playerControls_toolbar_rewind_forward'),
  );
  const backOrForwardTime = backOrForwardKey?.time || 10;

  const tracking = useTracking(
    videoTagRef.current,
    pipState.deviceId,
    pipState.media.group?.common?.id,
  );

  const {
    playing_pipControls_icon_exit,
    playing_pipControls_icon_backward,
    playing_pipControls_icon_pause,
    playing_pipControls_icon_foreward,
    playing_pipControls_icon_leave,
    playing_pipControls_icon_play,
  } = assets;

  const [controlsVisible, setControlsVisible] = useState(false);

  const position = useRef({
    pos1: 0,
    pos2: 0,
    pos3: 0,
    pos4: 0,
  });

  const windowSize = useWindowSize();

  const elementDrag = e => {
    e.preventDefault();
    position.current.pos1 = position.current.pos3 - e.clientX;
    position.current.pos2 = position.current.pos4 - e.clientY;
    position.current.pos3 = e.clientX;
    position.current.pos4 = e.clientY;
    if (pictureInPictueRef.current.offsetTop - position.current.pos2 < 0) {
      pictureInPictueRef.current.style.top = 0;
    } else if (
      pictureInPictueRef.current.offsetTop - position.current.pos2 >
      windowSize.height - pictureInPictueRef.current.offsetHeight
    ) {
      pictureInPictueRef.current.style.top =
        windowSize.height - pictureInPictueRef.current.offsetHeight + 'px';
    } else {
      pictureInPictueRef.current.style.top =
        pictureInPictueRef.current.offsetTop - position.current.pos2 + 'px';
    }
    if (pictureInPictueRef.current.offsetLeft - position.current.pos1 < 0) {
      pictureInPictueRef.current.style.left = 0;
    } else if (
      pictureInPictueRef.current.offsetLeft - position.current.pos1 >
      windowSize.width - pictureInPictueRef.current.offsetWidth
    ) {
      pictureInPictueRef.current.style.left =
        windowSize.width - pictureInPictueRef.current.offsetWidth + 'px';
    } else {
      pictureInPictueRef.current.style.left =
        pictureInPictueRef.current.offsetLeft - position.current.pos1 + 'px';
    }
  };

  const closeDragElement = () => {
    document.onmouseup = null;
    document.onmousemove = null;
  };

  const dragMouseDown = e => {
    e.preventDefault();
    position.current.pos3 = e.clientX;
    position.current.pos4 = e.clientY;
    document.onmouseup = closeDragElement;
    document.onmousemove = elementDrag;
  };

  const resetMedia = async () => {
    await shakaPlayerRef.current.unload();
    await shakaPlayerRef.current.destroy();
    shakaPlayerRef.current = null;
  };

  useEffect(
    () => {
      pictureInPictueRef.current.onmousedown = dragMouseDown;
    },
    [windowSize],
  );

  useEffect(() => {
    const load = async () => {
      const {
        media: { certificate_url },
      } = pipState.media;
      const language = getEncode(pipState.content);
      const streamType = language.encode;
      const isLive = false;
      const provedorCode =
        pipState.content.group.common.extendedcommon.media.proveedor.codigo;
      if (!isLive) {
        const provider =
          pipState.content.group?.common?.extendedcommon?.media?.proveedor
            ?.nombre;
        const title = pipState.media.group?.common?.title;
      }
      let certificate;
      try {
        const rC = await fetchCertificate(
          certificate_url,
          provedorCode,
          streamType,
          apaMediaCertificate,
          true,
        );
        const certBuffer = await rC.arrayBuffer();
        certificate = new Uint8Array(certBuffer);
      } catch (error) {
        certificate = null;
      }
      if (shakaPlayerRef.current) {
        await resetMedia();
      }
      shakaPlayerRef.current = new shaka.Player(videoTagRef.current);
      const player = shakaPlayerRef.current;
      if (certificate) {
        player.configure(
          'drm.advanced.com\\.widevine\\.alpha.serverCertificate',
          certificate,
        );
        player.configure(
          'drm.advanced.com\\.apple\\.fps\\.1_0.serverCertificate',
          certificate,
        );
      }
      const config = { ...DefaultPlaybackConfig };

      if (isLive) {
        config.manifest = {
          retryParameters: {
            timeout: 2e4,
            maxAttempts: 6,
            baseDelay: 500,
            backoffFactor: 2,
            fuzzFactor: 0.5,
          },
        };
      }

      const networkingEngine = player.getNetworkingEngine();
      networkingEngine.clearAllRequestFilters();
      networkingEngine.clearAllResponseFilters();

      if (streamType.includes('hls')) {
        player.configure('drm.initDataTransform', initData => {
          const skdUri = shaka.util.StringUtils.fromBytesAutoDetect(initData);
          const contentId = skdUri.substring(skdUri.indexOf('skd://') + 6);
          const cert = player.drmInfo().serverCertificate;

          const requestFilterConfig = {
            provedorCode,
            deviceId: pipState.deviceId,
            media: pipState.media,
            skdUri,
            streamType,
            player,
            contentId,
          };

          const responseFilterConfig = {
            provedorCode,
            streamType,
          };
          const requestFilter = getRequestFilters(requestFilterConfig);
          const responseFilter = getResponseFilters(responseFilterConfig);
          networkingEngine.registerRequestFilter(requestFilter);
          networkingEngine.registerResponseFilter(responseFilter);

          return shaka.util.FairPlayUtils.initDataTransform(
            initData,
            contentId,
            cert,
          );
        });
      } else {
        const requestFilterConfig = {
          provedorCode,
          deviceId: pipState.deviceId,
          media: pipState.media,
          streamType,
          player,
          isLive,
        };
        const responseFilterConfig = {
          provedorCode,
          streamType,
          player,
        };
        const requestFilter = getRequestFilters(requestFilterConfig);
        const responseFilter = getResponseFilters(responseFilterConfig);
        networkingEngine.registerRequestFilter(requestFilter);
        networkingEngine.registerResponseFilter(responseFilter);
      }

      config.drm.servers['com.widevine.alpha'] =
        pipState.media.media.server_url;
      config.drm.servers['com.microsoft.playready'] =
        pipState.media.media.server_url;
      // config.manifest.dash.clockSyncUri = media.media.video_url;
      player.configure(config);

      try {
        await player.load(pipState.media.media.video_url);
        const currentEncode = getEncode(pipState.content).encode;

        if (currentEncode.includes('_ma')) {
          if (!navigator.userAgent?.match(/version.*safari/i)) {
            if (currentAudioLanguage.audio === 'ORIGINAL') {
              player.selectAudioLanguage('or');
            } else if (currentAudioLanguage.option_name === 'dubbed') {
              player.selectAudioLanguage(
                currentAudioLanguage.audio.toLowerCase(),
              );
            }
          } else {
            if (currentAudioLanguage.audio === 'ORIGINAL') {
              videoTagRef.current.addEventListener(
                'play',
                pauseOnInitialPlay(() => {
                  player.selectAudioLanguage('or');
                  recalculateSubtitles(
                    videoTagRef.current,
                    currentAudioLanguage,
                  );
                }),
              );
            } else if (currentAudioLanguage.option_name === 'dubbed') {
              videoTagRef.current.addEventListener(
                'play',
                pauseOnInitialPlay(() => {
                  player.selectAudioLanguage(
                    currentAudioLanguage.audio.toLowerCase(),
                  );
                  recalculateSubtitles(
                    videoTagRef.current,
                    currentAudioLanguage,
                  );
                }),
              );
            }
          }
        }

        const setAvailableCaptionsToSRC = async () => {
          if (!media.media.subtitles) return;
          const { options } = media?.media?.subtitles;
          const currentSubtitle = currentAudioLanguage?.subtitle?.toLowerCase();
          const type = 'caption';
          const mime = 'text/vtt';
          shakaPlayerRef.current.setTextTrackVisibility(false);
          Object.keys(options).forEach(opt => {
            shakaPlayerRef.current.addTextTrackAsync(
              options[opt].external,
              options[opt].internal,
              type,
              mime,
            );
            if (currentSubtitle && options[opt].internal === currentSubtitle) {
              shakaPlayerRef.current.selectTextLanguage(currentSubtitle);
              shakaPlayerRef.current.setTextTrackVisibility(true);
            }
          });
        };

        const availableTextLanguages = player.getTextLanguages();
        if (!navigator.userAgent?.match(/version.*safari/i)) {
          setAvailableCaptionsToSRC();
        }
        dispatch(setAvailableCaptions(availableTextLanguages));

        setIsPlaying(true);
        videoTagRef.current.currentTime = pipState.time;
      } catch (error) {
        console.log(error);
      }
    };
    if (pipState.media) {
      load();
    }
  }, []);

  useEffect(() => {
    videoTagRef.current.addEventListener('timeupdate', event => {
      timeRef.current = event.target.currentTime;
      if (event.target.currentTime === event.target.duration) {
        tracking.handleEnd(parseInt(event.target.currentTime, 10));
        dispatch({
          type: 'CLEAR_PICTURE_IN_PICTURE',
        });
      }
    });

    return () => {
      tracking.handleStop(parseInt(videoTagRef.current.currentTime, 10));
      dispatch({
        type: 'CLEAR_PICTURE_IN_PICTURE',
      });
      videoTagRef.current?.removeEventListener('timeupdate', () => {});
    };
  }, []);

  return (
    <div
      ref={pictureInPictueRef}
      className="PictureInPicture"
      onMouseOver={() => {
        setControlsVisible(true);
      }}
      onMouseLeave={() => {
        setControlsVisible(false);
      }}
    >
      {controlsVisible && (
        <div
          style={{
            borderRadius: '15px',
            position: 'absolute',
            height: '100%',
            width: '100%',
            display: 'flex',
            zIndex: z_index?.controlPip,
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <button
              type="button"
              style={{
                background: 'none',
                color: 'inherit',
                border: 'none',
                padding: 0,
                font: 'inherit',
                cursor: 'pointer',
                outline: 'inherit',
                backgroundColor: '#494949',
                borderRadius: '100%',
                margin: '13px 0 0 13px',
              }}
              onClick={() => {
                dispatch({
                  type: 'CLEAR_PICTURE_IN_PICTURE',
                });
              }}
            >
              <img
                alt={'playing_pipControls_icon_exit'}
                src={playing_pipControls_icon_exit}
              />
            </button>
          </div>
          <div
            ref={pipControlsRef}
            style={{
              display: 'flex',
              justifyContent: 'center',
              marginBottom: '15px',
            }}
          >
            <button
              type="button"
              style={{
                background: 'none',
                color: 'inherit',
                border: 'none',
                padding: 0,
                font: 'inherit',
                cursor: 'pointer',
                outline: 'inherit',
                backgroundColor: '#4D4D4D',
                borderRadius: '100%',
                height: '45px',
                width: '45px',
                margin: '7px',
              }}
              onClick={() => {
                const newTime = videoTagRef.current.currentTime - backOrForwardTime;
                videoTagRef.current.currentTime = newTime;
                tracking.handleSeek();
              }}
            >
              <img
                alt={'playing_pipControls_icon_exit'}
                src={playing_pipControls_icon_backward}
              />
            </button>
            <button
              type="button"
              style={{
                background: 'none',
                color: 'inherit',
                border: 'none',
                padding: 0,
                font: 'inherit',
                cursor: 'pointer',
                outline: 'inherit',
                backgroundColor: '#4D4D4D',
                borderRadius: '100%',
                height: '45px',
                width: '45px',
                margin: '7px',
              }}
              onClick={() => {
                if (videoTagRef.current.paused) {
                  setIsPlaying(true);
                  return videoTagRef.current.play();
                }
                setIsPlaying(false);
                videoTagRef.current.pause();
              }}
            >
              <img
                alt={'playing_pipControls_icon_exit'}
                src={
                  !isPlaying
                    ? playing_pipControls_icon_play
                    : playing_pipControls_icon_pause
                }
              />
            </button>
            <button
              type="button"
              style={{
                background: 'none',
                color: 'inherit',
                border: 'none',
                padding: 0,
                font: 'inherit',
                cursor: 'pointer',
                outline: 'inherit',
                backgroundColor: '#4D4D4D',
                borderRadius: '100%',
                height: '45px',
                width: '45px',
                margin: '7px',
              }}
              onClick={() => {
                const newTime = videoTagRef.current.currentTime + backOrForwardTime;
                videoTagRef.current.currentTime = newTime;
                tracking.handleSeek();
              }}
            >
              <img
                alt={'playing_pipControls_icon_exit'}
                src={playing_pipControls_icon_foreward}
              />
            </button>
            <button
              type="button"
              style={{
                background: 'none',
                color: 'inherit',
                border: 'none',
                padding: 0,
                font: 'inherit',
                cursor: 'pointer',
                outline: 'inherit',
                backgroundColor: '#4D4D4D',
                borderRadius: '100%',
                height: '45px',
                width: '45px',
                margin: '7px',
              }}
              onClick={() => {
                localStorage.setItem(
                  'timeAtChangeLanguage',
                  videoTagRef.current.currentTime,
                );
                dispatch(setBookmarkDiscard(true));
                let urlPlayer = `/${GetRegion()}/watch/${
                  pipState.media.group?.common?.id
                }`;
                if (pipState.isPreview) {
                  urlPlayer = `${urlPlayer}?preview=true`;
                }
                navigate(urlPlayer, { replace: true });
              }}
            >
              <img
                alt={'playing_pipControls_icon_exit'}
                src={playing_pipControls_icon_leave}
              />
            </button>
          </div>
        </div>
      )}
      <PipPlayer videoTagRef={videoTagRef} />
    </div>
  );
};

export default PictureInPicture;
